import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Instagram, 
  Facebook, 
  ExternalLink,
  Star,
  Quote,
  ChevronDown,
  Menu,
  X,
  ShoppingBag,
  Calendar,
  Shield,
  Target,
  Award,
  Heart,
  Check,
  Gift,
  Users,
  Share2,
  Copy,
  Play
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets - Real Photos
const LOGO_URL = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/4mmlrrbv_VV%20LOGO%20%20%2819%29.png";
const FLAG_URL = "https://customer-assets.emergentagent.com/job_91ab2b3f-c27e-45dd-920a-fba9ecba4c41/artifacts/bauosyuf_Untitled%20design.PNG";

// Tirrell's Headshots
const HEADSHOT_MAIN = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/t2urva5s_AGTeamShootEduts-57.jpg";
const HEADSHOT_PHONE = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/50ajqzfv_AGTeamShootEduts-54.jpg";
const HEADSHOT_DOOR = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/pqdyvwie_1.jpg";

// LPT Realty Logo
const LPT_LOGO = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/0t1ym5uz_LPT-Realty-Logo-White.webp";

// Family & Client Photos
const FAMILY_PHOTO = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/qcxfv6yo_IMG_4091.JPG";
const HOUSE_LISTING = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/a819ssha_IMG_2605.jpg";

// Real Client Closing Photos with Reviews
const CLIENT_CARTER = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/8b7zehm8_Untitled%20design.jpg";
const CLIENT_IZUCHUKWU = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/lktecw8r_IMG_9759.jpeg";
const CLIENT_COUPLE = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/vf5a14cc_IMG_0582.jpeg";
const CLIENT_DIANA = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/svpn01lv_IMG_5915.jpeg";
const CLIENT_LADIES = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/fdsx2wee_Untitled%20design.png";

// Videos
const VIDEO_HOME = "https://customer-assets.emergentagent.com/job_lpt-realty-veterans/artifacts/jq8pu6ss_IMG_3792.MOV";

// Hero background - using house listing photo
const HERO_BG = HOUSE_LISTING;

// Navigation
const Navigation = ({ isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Referrals", href: "#referrals" },
    { name: "Reviews", href: "#reviews" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-black shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Veteran Vision" className="h-10 w-auto" />
            <span className="text-white font-heading text-xl hidden sm:block">VETERAN VISION</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#booking"
              className="bg-white text-black px-6 py-2 uppercase tracking-widest text-sm font-accent hover:bg-neutral-200 transition-colors"
              data-testid="nav-book-btn"
            >
              Book Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white z-[60] relative"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu md:hidden" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-white text-2xl font-accent uppercase tracking-widest"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.name}
            </a>
          ))}
          <a
            href="#booking"
            className="bg-white text-black px-8 py-3 uppercase tracking-widest text-lg font-accent"
            onClick={() => setMobileMenuOpen(false)}
          >
            Book Now
          </a>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="hero-section relative flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="hero-overlay" />
      
      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
        <p className="font-accent uppercase tracking-[0.3em] text-sm md:text-base mb-6 animate-fade-in">
          Our Mission is Your Legacy
        </p>
        <h1 
          className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tight animate-slide-up"
          data-testid="hero-title"
        >
          Discover Your<br />Dream Home
        </h1>
        <p className="font-body text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto mb-12 leading-relaxed">
          At Veteran Vision, real estate isn't about transactions—it's about service.
          I bring the same discipline, loyalty, and attention to detail I learned in the military to every client I serve.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href="#booking"
            className="btn-primary w-full sm:w-auto"
            data-testid="hero-cta-book"
          >
            Schedule Consultation
          </a>
          <a
            href="#about"
            className="btn-secondary border-white text-white hover:bg-white hover:text-black w-full sm:w-auto"
            data-testid="hero-cta-learn"
          >
            Learn More
          </a>
        </div>

        {/* Powered by LPT Realty */}
        <div className="mt-16 flex items-center justify-center gap-4">
          <span className="text-neutral-400 text-sm uppercase tracking-wider">Powered by</span>
          <img src={LPT_LOGO} alt="LPT Realty" className="h-10" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-white w-8 h-8" />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-white" data-testid="about-section">
      <div className="container mx-auto px-4 md:px-6">
        {/* Bio Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-24">
          <div className="order-2 md:order-1">
            <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
              Meet Your Agent
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-6">
              Tirrell Anderson
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-6">
              I founded Veteran Vision Real Estate on a singular belief: service doesn't end when the uniform comes off. 
              Based in Charlotte and serving the Carolinas, my company is built on the elite standards of discipline, 
              strategy, and long-term vision that I honed during my service in the United States Air Force.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-8">
              Whether you're buying, selling, or building, I'm here to guide you with purpose and protect your best interests—so you can find a place you're proud to call home.
            </p>
            
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="px-4 py-2 bg-neutral-100 font-accent uppercase tracking-wider">NC Licensed</span>
              <span className="px-4 py-2 bg-neutral-100 font-accent uppercase tracking-wider">SC Licensed</span>
              <span className="px-4 py-2 bg-neutral-100 font-accent uppercase tracking-wider">AZ Licensed</span>
              <span className="px-4 py-2 bg-neutral-100 font-accent uppercase tracking-wider">REALTOR®</span>
            </div>
          </div>
          
          <div className="order-1 md:order-2">
            <div className="relative">
              <img 
                src={HEADSHOT_MAIN}
                alt="Tirrell Anderson"
                className="w-full"
                data-testid="agent-photo"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black text-white p-6">
                <p className="font-heading italic text-lg">
                  "I don't stop working when I'm tired, I stop when I'm done."
                </p>
                <p className="text-neutral-400 text-sm mt-2">— David Goggins</p>
              </div>
            </div>
          </div>
        </div>

        {/* Brand Philosophy */}
        <div className="mb-24" data-testid="philosophy-section">
          <div className="text-center mb-16">
            <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
              Our Commitment
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold">
              Brand Philosophy
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Target className="w-8 h-8" />,
                title: "Mission Over Emotion",
                description: "We remove the noise and focus on the data. Every move must serve your larger objective."
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Discipline Creates Wealth",
                description: "Consistency, preparation, and negotiation strength are the tools we use to protect your generational assets."
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: "Elevated Service is the Standard",
                description: "Communication is proactive, details are paramount, and excellence is the baseline."
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: "Legacy is the Goal",
                description: "Homes are the foundations for families, businesses, and future opportunities."
              }
            ].map((pillar, index) => (
              <div 
                key={index}
                className="pillar-card"
                data-testid={`pillar-card-${index}`}
              >
                <div className="mb-4">{pillar.icon}</div>
                <h3 className="font-heading text-xl font-semibold mb-3">{pillar.title}</h3>
                <p className="text-sm leading-relaxed opacity-80">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Family Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-testid="family-section">
          <div>
            <img 
              src={FAMILY_PHOTO}
              alt="Tirrell Anderson Family"
              className="w-full"
            />
          </div>
          <div>
            <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
              A Global Perspective on Growth
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-6">
              Family-Centered Mindset
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-6">
              My drive is fueled by a commitment to entrepreneurship and a deep-rooted focus on family. 
              My wife immigrated from Poland and built a thriving business here in Charlotte; watching her 
              create something from nothing reinforced my belief that ownership changes everything.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Our children, Leo and Eva—dual citizens of the United States and Poland—are growing up in a 
              household built on resilience and a global perspective. They are the "why" behind the brand I 
              am building. This family-centered mindset allows me to connect deeply with clients who are 
              working to build their own piece of the American Dream.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Veteran Vision Standard Section
const StandardSection = () => {
  return (
    <section className="py-24 md:py-32 bg-black text-white relative noise-overlay" data-testid="standard-section">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <img src={FLAG_URL} alt="American Flag" className="w-32 h-auto mx-auto mb-8 invert" />
          <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-400 mb-4">
            Excellence as Standard
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold mb-8">
            The Veteran Vision Standard
          </h2>
          <p className="text-xl text-neutral-300 leading-relaxed mb-8">
            My goal is to scale Veteran Vision into a nationally recognized brand rooted in integrity 
            and elite performance. When you work with me, you aren't just hiring an agent; you are 
            gaining a partner who operates with a long-term mindset and unwavering precision.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-lg font-accent uppercase tracking-wider">
            <span>Mission-first</span>
            <span className="text-neutral-500">•</span>
            <span>Family-centered</span>
            <span className="text-neutral-500">•</span>
            <span>Results-driven</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section with BPO Payment
const ServicesSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });

  const handleBPOPayment = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/bpo/checkout`, {
        origin_url: window.location.origin,
        name: customerInfo.name,
        email: customerInfo.email,
        phone: customerInfo.phone
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to initiate payment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="services" className="py-24 md:py-32 bg-neutral-50" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
            What We Offer
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
            Explore Our Services
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Ready to find your dream home without the stress? Let us turn house hunting into a thrilling 
            adventure complete with surprises and smiles at every corner.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Buyer Services */}
          <div className="service-card" data-testid="service-buyer">
            <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-2xl font-semibold mb-4">Buyer Representation</h3>
            <p className="text-neutral-600 mb-6">
              Expert guidance through the entire home buying process, from search to closing.
            </p>
            <ul className="text-left text-sm text-neutral-600 space-y-2">
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> New Construction</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Luxury Properties</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Investment Real Estate</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> First-Time Buyers</li>
            </ul>
          </div>

          {/* Seller Services */}
          <div className="service-card" data-testid="service-seller">
            <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6">
              <Target className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-2xl font-semibold mb-4">Seller Representation</h3>
            <p className="text-neutral-600 mb-6">
              Strategic marketing and negotiation to maximize your property's value.
            </p>
            <ul className="text-left text-sm text-neutral-600 space-y-2">
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Market Analysis</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Professional Staging</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Expert Negotiation</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Seamless Closing</li>
            </ul>
          </div>

          {/* BPO Service - Featured */}
          <div className="service-card border-2 border-black relative" data-testid="service-bpo">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-1 text-xs font-accent uppercase tracking-wider">
              Popular
            </div>
            <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6">
              <Award className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-2xl font-semibold mb-4">Broker Price Opinion</h3>
            <p className="text-4xl font-heading font-bold mb-4">$125</p>
            <p className="text-neutral-600 mb-6 text-sm">
              A Broker Price Opinion (BPO) is a professional estimate of a property's market value, 
              prepared by a licensed real estate broker. It includes comparative market analysis, 
              property condition assessment, and local market insights.
            </p>
            
            <div className="space-y-3 mb-6">
              <input
                type="text"
                placeholder="Your Name"
                className="input-underline"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                data-testid="bpo-name-input"
              />
              <input
                type="email"
                placeholder="Email Address"
                className="input-underline"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                data-testid="bpo-email-input"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="input-underline"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                data-testid="bpo-phone-input"
              />
            </div>
            
            <button
              onClick={handleBPOPayment}
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="bpo-pay-btn"
            >
              {isLoading ? (
                <span className="spinner" />
              ) : (
                <>
                  Pay Now - $125
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Referral Program Section
const ReferralSection = () => {
  const [referralForm, setReferralForm] = useState({
    referrerName: "",
    referrerEmail: "",
    referrerPhone: "",
    friendName: "",
    friendEmail: "",
    friendPhone: "",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const referralLink = "https://veteran-vision.com/?ref=friend";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/referral`, referralForm);
      toast.success("Referral submitted! We'll reach out to your friend soon.");
      setReferralForm({
        referrerName: "",
        referrerEmail: "",
        referrerPhone: "",
        friendName: "",
        friendEmail: "",
        friendPhone: "",
        notes: ""
      });
    } catch (error) {
      console.error("Error submitting referral:", error);
      toast.error("Failed to submit referral. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral link copied to clipboard!");
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <section id="referrals" className="py-24 md:py-32 bg-black text-white relative noise-overlay" data-testid="referral-section">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-white text-black flex items-center justify-center mx-auto mb-6">
              <Gift className="w-10 h-10" />
            </div>
            <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-400 mb-4">
              Refer a Friend Program
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Earn $500 for Every Referral
            </h2>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
              Know someone looking to buy or sell a home? Refer them to Veteran Vision and receive a 
              <span className="text-white font-semibold"> $500 gift card at closing</span>. 
              It's our way of saying thank you for trusting us with your friends and family.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* How It Works */}
            <div>
              <h3 className="font-heading text-2xl font-semibold mb-8">How It Works</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white text-black flex items-center justify-center flex-shrink-0 font-heading text-xl font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Submit a Referral</h4>
                    <p className="text-neutral-400">Fill out the form with your friend's information or share your unique referral link.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white text-black flex items-center justify-center flex-shrink-0 font-heading text-xl font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">We Reach Out</h4>
                    <p className="text-neutral-400">I'll personally contact your friend and provide the same elite service you experienced.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white text-black flex items-center justify-center flex-shrink-0 font-heading text-xl font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Get Rewarded</h4>
                    <p className="text-neutral-400">Once your friend closes on their home, you'll receive a $500 gift card as our thank you.</p>
                  </div>
                </div>
              </div>

              {/* Share Link */}
              <div className="mt-10 p-6 border border-neutral-700">
                <h4 className="font-accent uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Share Your Referral Link
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 bg-neutral-900 border border-neutral-700 px-4 py-3 text-sm"
                    data-testid="referral-link-input"
                  />
                  <button
                    onClick={copyLink}
                    className="bg-white text-black px-6 py-3 flex items-center gap-2 hover:bg-neutral-200 transition-colors"
                    data-testid="copy-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            </div>

            {/* Referral Form */}
            <div className="bg-white text-black p-8">
              <h3 className="font-heading text-2xl font-semibold mb-6 flex items-center gap-2">
                <Users className="w-6 h-6" /> Submit a Referral
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="referral-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Your Name</label>
                    <input
                      type="text"
                      required
                      className="input-underline"
                      value={referralForm.referrerName}
                      onChange={(e) => setReferralForm({...referralForm, referrerName: e.target.value})}
                      data-testid="referrer-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Your Email</label>
                    <input
                      type="email"
                      required
                      className="input-underline"
                      value={referralForm.referrerEmail}
                      onChange={(e) => setReferralForm({...referralForm, referrerEmail: e.target.value})}
                      data-testid="referrer-email"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Your Phone</label>
                  <input
                    type="tel"
                    required
                    className="input-underline"
                    value={referralForm.referrerPhone}
                    onChange={(e) => setReferralForm({...referralForm, referrerPhone: e.target.value})}
                    data-testid="referrer-phone"
                  />
                </div>

                <hr className="my-6 border-neutral-200" />
                
                <p className="text-sm text-neutral-500 mb-2">Friend's Information</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Friend's Name</label>
                    <input
                      type="text"
                      required
                      className="input-underline"
                      value={referralForm.friendName}
                      onChange={(e) => setReferralForm({...referralForm, friendName: e.target.value})}
                      data-testid="friend-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Friend's Email</label>
                    <input
                      type="email"
                      required
                      className="input-underline"
                      value={referralForm.friendEmail}
                      onChange={(e) => setReferralForm({...referralForm, friendEmail: e.target.value})}
                      data-testid="friend-email"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Friend's Phone</label>
                  <input
                    type="tel"
                    required
                    className="input-underline"
                    value={referralForm.friendPhone}
                    onChange={(e) => setReferralForm({...referralForm, friendPhone: e.target.value})}
                    data-testid="friend-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Additional Notes (Optional)</label>
                  <textarea
                    className="input-underline resize-none"
                    rows={3}
                    placeholder="Anything we should know about your friend's situation?"
                    value={referralForm.notes}
                    onChange={(e) => setReferralForm({...referralForm, notes: e.target.value})}
                    data-testid="referral-notes"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                  data-testid="referral-submit-btn"
                >
                  {isSubmitting ? "Submitting..." : "Submit Referral"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Client Gallery Section
const ClientGallerySection = () => {
  const [playingVideo, setPlayingVideo] = useState(false);

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="gallery-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
            Making Dreams Come True
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
            Client Success Gallery
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Every client has a story. Here are some of the moments we've shared together on the journey to finding their perfect home.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Video Card */}
          <div className="relative group overflow-hidden aspect-video col-span-1 lg:col-span-2" data-testid="video-card">
            <video
              src={VIDEO_HOME}
              className="w-full h-full object-cover"
              poster={HOUSE_LISTING}
              controls={playingVideo}
              muted
              playsInline
              onClick={() => setPlayingVideo(true)}
            />
            {!playingVideo && (
              <div 
                className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer hover:bg-black/30 transition-colors"
                onClick={() => setPlayingVideo(true)}
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-black ml-1" />
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white font-semibold">New Home Walkthrough</p>
            </div>
          </div>

          {/* Carter - Lazer Edge Gift */}
          <div className="relative group overflow-hidden aspect-square" data-testid="client-carter-photo">
            <img 
              src={CLIENT_CARTER}
              alt="Carter receiving Charlotte map gift at closing"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white font-semibold">Carter - West Blvd Closing</p>
            </div>
          </div>

          {/* Izuchukwu - SOLD Key */}
          <div className="relative group overflow-hidden aspect-square" data-testid="client-izuchukwu-photo">
            <img 
              src={CLIENT_IZUCHUKWU}
              alt="Izuchukwu Okonkwo with SOLD key"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white font-semibold">Izuchukwu - New Home 2026</p>
            </div>
          </div>

          {/* Edgar & Sole Bermudez - SOLD Key */}
          <div className="relative group overflow-hidden aspect-square" data-testid="client-couple-photo">
            <img 
              src={CLIENT_COUPLE}
              alt="Edgar and Sole Bermudez with SOLD key"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white font-semibold">Edgar & Sole Bermudez</p>
            </div>
          </div>

          {/* Diana Arevalo - Lazer Edge Gift */}
          <div className="relative group overflow-hidden aspect-square" data-testid="client-diana-photo">
            <img 
              src={CLIENT_DIANA}
              alt="Diana Arevalo with Charlotte map gift"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white font-semibold">Diana - Coulwood West Closing</p>
            </div>
          </div>

          {/* Ladies - Lazer Edge Gift */}
          <div className="relative group overflow-hidden aspect-square col-span-1 md:col-span-2 lg:col-span-1" data-testid="client-ladies-photo">
            <img 
              src={CLIENT_LADIES}
              alt="Happy clients with Charlotte map gift"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white font-semibold">Dream Home Achieved!</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Booking Section with Calendly
const BookingSection = () => {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <section id="booking" className="py-24 md:py-32 bg-white" data-testid="booking-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
              Let's Connect
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Grab Your Spot
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed mb-8">
              Ready to embark on your real estate adventure? Schedule an appointment with me today 
              and let's find the home that's just right for you!
            </p>
            <img 
              src={HEADSHOT_PHONE}
              alt="Tirrell Anderson"
              className="w-full max-w-md hidden lg:block"
            />
          </div>
          
          <div 
            className="calendly-inline-widget"
            data-url="https://calendly.com/tirrell-veteran-vision?background_color=ffffff&text_color=000000&primary_color=000000"
            data-testid="calendly-embed"
          />
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };
    fetchReviews();
  }, []);

  return (
    <section id="reviews" className="py-24 md:py-32 bg-neutral-50" data-testid="reviews-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
            Client Success Stories
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
            What Clients Say
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-black text-black" />
            ))}
          </div>
          <p className="text-neutral-600">5.0 Rating on Zillow • 8 Reviews</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reviews.slice(0, 6).map((review, index) => (
            <div 
              key={review.id || index}
              className={`review-card ${review.photo ? 'flex flex-col' : ''}`}
              data-testid={`review-card-${index}`}
            >
              {review.photo && (
                <div className="mb-6 -mx-8 -mt-8">
                  <img 
                    src={review.photo} 
                    alt={`${review.author} closing`}
                    className="w-full h-64 object-cover"
                  />
                </div>
              )}
              <Quote className="w-8 h-8 text-neutral-300 mb-4" />
              <p className="text-neutral-700 leading-relaxed mb-6 text-sm">
                "{review.text.length > 300 ? review.text.slice(0, 300) + '...' : review.text}"
              </p>
              <div className="flex items-center justify-between mt-auto">
                <div>
                  <p className="font-semibold">{review.author}</p>
                  <p className="text-xs text-neutral-500">{review.type}</p>
                </div>
                <div className="flex gap-1">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-black text-black" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://www.zillow.com/profile/Tirrell%20Anderson"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="zillow-link"
          >
            View All Reviews on Zillow
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Message sent successfully! We'll get back to you soon.");
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="font-accent uppercase tracking-[0.2em] text-sm text-neutral-500 mb-4">
              Get In Touch
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-6">
              Let's Build Your Legacy Together
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-8">
              I am here to guide your next move with the foresight and discipline it deserves. 
              Reach out today to start your real estate journey.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black text-white flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Phone</p>
                  <a href="tel:602-228-9029" className="font-semibold hover:underline">602-228-9029</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black text-white flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Email</p>
                  <a href="mailto:tirrell@veteran-vision.com" className="font-semibold hover:underline">tirrell@veteran-vision.com</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black text-white flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Service Areas</p>
                  <p className="font-semibold">Charlotte, NC • Carolinas • Arizona</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  required
                  className="input-underline"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  className="input-underline"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  required
                  className="input-underline"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <textarea
                  placeholder="Your Message"
                  rows={4}
                  required
                  className="input-underline resize-none"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Veteran Vision" className="h-14 w-auto" />
              <div>
                <p className="font-heading text-2xl font-bold">VETERAN VISION</p>
                <p className="text-neutral-400 text-sm">Powered by LPT Realty</p>
              </div>
            </div>
            <p className="text-neutral-400 leading-relaxed mb-6">
              Real estate is more than a transaction; it is a mission. Whether you are acquiring 
              a luxury residence, navigating new construction, or securing a strategic investment, 
              you aren't just buying property—you are making a legacy decision.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/tirrellsellsclt"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/veteranvision/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://shop.app/m/bp6cefraug"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                data-testid="social-shop"
              >
                <ShoppingBag className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-accent uppercase tracking-wider text-sm mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#about" className="footer-link">About</a></li>
              <li><a href="#services" className="footer-link">Services</a></li>
              <li><a href="#reviews" className="footer-link">Reviews</a></li>
              <li><a href="#booking" className="footer-link">Book Consultation</a></li>
              <li><a href="#contact" className="footer-link">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-accent uppercase tracking-wider text-sm mb-6">Contact</h4>
            <ul className="space-y-3 text-neutral-400">
              <li>
                <a href="tel:602-228-9029" className="footer-link flex items-center gap-2">
                  <Phone className="w-4 h-4" /> 602-228-9029
                </a>
              </li>
              <li>
                <a href="mailto:tirrell@veteran-vision.com" className="footer-link flex items-center gap-2">
                  <Mail className="w-4 h-4" /> tirrell@veteran-vision.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Charlotte, NC
              </li>
            </ul>
            
            <div className="mt-6">
              <a
                href="https://shop.app/m/bp6cefraug"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white hover:text-neutral-300 transition-colors"
                data-testid="shop-link"
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Shop Veteran Vision Merch</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            © {new Date().getFullYear()} Veteran Vision Real Estate. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <img src={LPT_LOGO} alt="LPT Realty" className="h-8" />
            <span className="text-neutral-500 text-sm">Licensed in NC, SC, & AZ</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main HomePage Component
const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div data-testid="home-page">
      <Navigation isScrolled={isScrolled} />
      <HeroSection />
      <AboutSection />
      <StandardSection />
      <ServicesSection />
      <ReferralSection />
      <ClientGallerySection />
      <BookingSection />
      <ReviewsSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default HomePage;
